import { Trans } from '@lingui/macro'
import { ButtonPrimary } from 'components/Button'
import { DarkCard, LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import { RowBetween, RowFixed } from 'components/Row'
import { SwitchLocaleLink } from 'components/SwitchLocaleLink'
import { STAKE_POOL_ADDRESS } from 'constants/addresses'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useDistributorContract, usePoolContract } from 'hooks/useContract'
import { darken } from 'polished'
import { useCallback, useMemo, useState } from 'react'
import { ExternalLink as ExternalLinkIcon } from 'react-feather'
import { useWalletModalToggle } from 'state/application/hooks'
import { useDarkModeManager } from 'state/user/hooks'
import styled from 'styled-components/macro'
import { CustomLightSpinner, ExternalLink, ThemedText } from 'theme'
import { BIG_ONE } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import { isChainAllowed } from 'utils/switchChain'
import { isMobile } from 'utils/userAgent'

import Charts from './components/Charts'
import { StakeModal } from './components/StakeModal'
import useDistributorData from './hooks/useDistributorData'
import usePublicPoolData from './hooks/usePublicPoolData'
import useSwcPrice from './hooks/useSwcPrice'
import useSwcSupply from './hooks/useSwcSupply'
import useWalletBalances from './hooks/useWalletBalances'
import useWalletPoolData from './hooks/useWalletPoolData'

const PageWrapper = styled.div`
  min-width: 800px;
  max-width: 960px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    min-width: 680px;
    max-width: 680px;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    min-width: 600px;
    max-width: 600px;
  `};

  @media only screen and (max-width: 620px) {
    min-width: 500px;
    max-width: 500px;
  }

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    min-width: 340px;
    max-width: 340px;
  `};
`

const Label = styled(({ end, ...props }) => <ThemedText.Label {...props} />)<{ end?: boolean }>`
  display: flex;
  font-size: 16px;
  justify-content: ${({ end }) => (end ? 'flex-end' : 'flex-start')};
  align-items: center;
`

const ResponsiveRow = styled(RowBetween)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    align-items: flex-start;
    row-gap: 16px;
    width: 100%:
  `};
`

const Row = styled(RowBetween)`
  background: ${({ theme }) => theme.bg0};
  border-radius: 8px;
  border: 1px solid #8247e5;
  padding: 16px;
  margin-bottom: 1rem;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    row-gap: 16px;
    width: 100%:
  `};
`

const Card = styled(DarkCard)`
  background: ${({ theme }) => theme.bg0};
  border-radius: 8px;
  border: 1px solid #8247e5;
`

const Title = styled(ThemedText.LargeHeader)<{ isDark?: boolean }>`
  background: ${({ isDark }) =>
    isDark
      ? `-webkit-linear-gradient(
    90deg,
    rgba(255, 252, 2, 1) 0%,
    rgba(255, 172, 5, 1) 45%,
    rgba(255, 255, 255, 1) 50%,
    rgba(242, 49, 222, 1) 55%,
    rgba(153, 22, 255, 1) 100%
  )`
      : `-webkit-linear-gradient(
        90deg,
        rgba(255, 172, 5, 1) 48%,
        rgba(255, 255, 255, 1) 50%,
        rgba(242, 49, 222, 1) 52%,
        rgba(153, 22, 255, 1) 60%
      )`};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const ButtonStake = styled(ButtonPrimary)`
  background: #2172e5;
`

const ButtonUnstake = styled(ButtonPrimary)`
  background: #df1f38;
  &:focus {
    box-shadow: 0 0 0 1pt ${darken(0.05, '#df1f38')};
    background-color: ${darken(0.05, '#df1f38')};
  }
  &:hover {
    background-color: ${darken(0.05, '#df1f38')};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${darken(0.05, '#df1f38')};
    background-color: ${darken(0.05, '#df1f38')};
  }
`

const ButtonClaim = styled(ButtonPrimary)`
  background: #25b728;
  &:focus {
    box-shadow: 0 0 0 1pt ${darken(0.05, '#25b728')};
    background-color: ${darken(0.05, '#25b728')};
  }
  &:hover {
    background-color: ${darken(0.05, '#25b728')};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${darken(0.05, '#25b728')};
    background-color: ${darken(0.05, '#25b728')};
  }
`

export default function Stake() {
  const { account, chainId, connector } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const [isDark] = useDarkModeManager()

  const chainAllowed = chainId && isChainAllowed(connector, chainId)
  const activeChain = chainAllowed ? chainId : 56

  const [unStaking, setUnStaking] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [distributing, setDistributing] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const handleOpenModal = () => {
    setOpenModal(true)
  }

  const poolContract = usePoolContract()
  const distributorContract = useDistributorContract()
  const walletData = useWalletBalances(activeChain)
  const supplyData = useSwcSupply(activeChain)
  const poolData = usePublicPoolData(activeChain)
  const walletPoolData = useWalletPoolData(activeChain)
  const swcPrice = useSwcPrice(activeChain)
  const canDistribute = useDistributorData(activeChain)

  const [distributionAvailable, setDistributionAvailable] = useState(canDistribute)

  const { totalHolders, totalStaked, totalDistributed, rewardsXblock } = poolData
  const { isInPool, stakedBal, pendingBal } = walletPoolData

  const apy = useMemo(() => {
    const BLOCKS_PER_YEAR = (60 / 3) * 60 * 24 * 365 // 10512000
    const totalRewardPricePerYear = BIG_ONE.times(rewardsXblock).times(BLOCKS_PER_YEAR)
    const totalStakingTokenInPool = swcPrice.times(totalStaked)
    const apr = totalRewardPricePerYear.div(totalStakingTokenInPool).times(100)
    return apr.isNaN() || !apr.isFinite() ? null : apr.toNumber()
  }, [rewardsXblock, swcPrice, totalStaked])

  const handleDistribute = useCallback(async () => {
    try {
      setDistributing(true)
      const tx = await distributorContract?.distributeRewards()
      await tx?.wait()
      poolData.fetchPublicPoolData()
      walletPoolData.fetchWalletPoolData()
      setDistributing(false)
      setDistributionAvailable(false)
    } catch (e) {
      setDistributing(false)
      setDistributionAvailable(false)
      console.error(e)
    } finally {
      setDistributing(false)
    }
  }, [distributorContract, poolData, walletPoolData])

  const handleUnStake = useCallback(async () => {
    try {
      setUnStaking(true)
      const tx = await poolContract?.removeStake()
      await tx?.wait()
      walletData.fetchWalletData()
      poolData.fetchPublicPoolData()
      walletPoolData.fetchWalletPoolData()
      setUnStaking(false)
    } catch (e) {
      setUnStaking(false)
      console.error(e)
    } finally {
      setUnStaking(false)
    }
  }, [poolContract, poolData, walletData, walletPoolData])

  const handleClaim = useCallback(async () => {
    try {
      setClaiming(true)
      const tx = await poolContract?.harvest()
      await tx?.wait()
      walletData.fetchWalletData()
      poolData.fetchPublicPoolData()
      walletPoolData.fetchWalletPoolData()
      setClaiming(false)
    } catch (e) {
      setClaiming(false)
      console.error(e)
    } finally {
      setClaiming(false)
    }
  }, [poolContract, poolData, walletData, walletPoolData])

  return (
    <>
      <PageWrapper>
        <AutoColumn gap="md">
          <AutoColumn gap="sm">
            {/* {distributionAvailable && (
              <Row>
                <AutoRow justify={isMobile ? 'center' : 'left'}>
                  <img src={MaticIcon} alt="matic-logo" width={24} />
                  <ThemedText.LargeHeader fontSize={isMobile ? 14 : 16} textAlign="center" ml="4px">
                    <Trans>Rewards pending distribution</Trans>
                  </ThemedText.LargeHeader>
                </AutoRow>
                <ButtonYellow width="fit-content" size="10px" disabled={distributing} onClick={handleDistribute}>
                  {distributing ? (
                    <>
                      <Trans>Distributing</Trans>
                      <CustomLightSpinner
                        src="/images/blue-loader.svg"
                        alt="loader"
                        size={'16px'}
                        style={{ marginLeft: '0.5rem' }}
                      />
                    </>
                  ) : (
                    <Trans>Distribute</Trans>
                  )}
                </ButtonYellow>
              </Row>
            )} */}
            <RowBetween>
              <RowFixed>
                <Title isDark={isDark} fontSize={isMobile ? 18 : 36} mr={2}>
                  <Trans>Stake</Trans>
                </Title>
              </RowFixed>
              {rewardsXblock.gt(0) && (
                <RowFixed gap="0.5rem">
                  <Title isDark={isDark} fontSize={isMobile ? 12 : 24} mr={2}>
                    APY:
                  </Title>
                  <Title isDark={isDark} fontSize={isMobile ? 12 : 24} mr={2}>
                    <> {apy?.toFixed(2)} %</>
                  </Title>
                </RowFixed>
              )}
            </RowBetween>
          </AutoColumn>
          <ResponsiveRow align="flex-start">
            <Card
              width="100%"
              height="100%"
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'space-between',
                marginRight: '12px',
              }}
            >
              <Charts supplyData={supplyData} />
              <ExternalLink
                id="pool-link"
                href={getExplorerLink(activeChain, STAKE_POOL_ADDRESS[activeChain], ExplorerDataType.ADDRESS)}
                target="_blank"
              >
                <ThemedText.Main mt="1rem">
                  <Trans>Pool contract</Trans>
                  <ExternalLinkIcon opacity={0.6} size={16} style={{ marginLeft: 5, marginBottom: -2 }} />
                </ThemedText.Main>
              </ExternalLink>
            </Card>
            <AutoColumn gap="sm" style={{ width: '100%', height: '100%' }}>
              <Card>
                <AutoColumn gap="md" style={{ width: '100%' }}>
                  <RowBetween style={{ alignItems: 'flex-start' }}>
                    <AutoColumn gap="md">
                      <Label>
                        <Trans>Pool Info</Trans>
                      </Label>
                    </AutoColumn>
                  </RowBetween>
                  <LightCard padding="12px 16px">
                    <AutoColumn gap="lg">
                      <RowBetween>
                        <ThemedText.Main>
                          <Trans>Total Distributed</Trans>
                        </ThemedText.Main>
                        <ThemedText.SubHeader>{getBalanceAmount(totalDistributed).toFixed(2)}</ThemedText.SubHeader>
                      </RowBetween>
                      <RowBetween>
                        <ThemedText.Main>
                          <Trans>Total Staked</Trans>
                        </ThemedText.Main>
                        <ThemedText.SubHeader>{getBalanceAmount(totalStaked).toFixed(2)}</ThemedText.SubHeader>
                      </RowBetween>
                      <RowBetween>
                        <ThemedText.Main>
                          <Trans>Holders</Trans>
                        </ThemedText.Main>
                        <ThemedText.SubHeader>{totalHolders.toString()}</ThemedText.SubHeader>
                      </RowBetween>
                    </AutoColumn>
                  </LightCard>
                </AutoColumn>
              </Card>
              <Card>
                <AutoColumn gap="md" style={{ width: '100%' }}>
                  <AutoColumn gap="md">
                    <RowBetween style={{ alignItems: 'flex-start' }}>
                      <AutoColumn gap="md">
                        <Label>
                          <Trans>Wallet Info</Trans>
                        </Label>
                      </AutoColumn>
                    </RowBetween>
                  </AutoColumn>
                  {account ? (
                    <LightCard padding="12px 16px">
                      <AutoColumn gap="lg">
                        <RowBetween>
                          <ThemedText.Main>
                            <Trans>Staked</Trans>
                          </ThemedText.Main>
                          <ThemedText.SubHeader>{getBalanceAmount(stakedBal).toString()} SWC</ThemedText.SubHeader>
                        </RowBetween>
                        <RowBetween>
                          <ThemedText.Main>
                            <Trans>Rewards</Trans>
                          </ThemedText.Main>
                          <ThemedText.SubHeader>{getBalanceAmount(pendingBal).toString()} MATIC</ThemedText.SubHeader>
                        </RowBetween>
                        {isInPool ? (
                          <AutoColumn gap="1rem">
                            <ButtonClaim size="10px" disabled={pendingBal.eq(0) || claiming} onClick={handleClaim}>
                              {claiming ? (
                                <>
                                  <Trans>Claiming</Trans>
                                  <CustomLightSpinner
                                    src="/images/blue-loader.svg"
                                    alt="loader"
                                    size={'16px'}
                                    style={{ marginLeft: '0.5rem' }}
                                  />
                                </>
                              ) : (
                                <Trans>Claim</Trans>
                              )}
                            </ButtonClaim>
                            <RowBetween>
                              <ButtonStake size="10px" width="45%" onClick={handleOpenModal}>
                                <Trans>Stake</Trans>
                              </ButtonStake>
                              <ButtonUnstake size="10px" width="45%" disabled={unStaking} onClick={handleUnStake}>
                                {unStaking ? (
                                  <>
                                    <Trans>Removing</Trans>
                                    <CustomLightSpinner
                                      src="/images/blue-loader.svg"
                                      alt="loader"
                                      size={'16px'}
                                      style={{ marginLeft: '0.5rem' }}
                                    />
                                  </>
                                ) : (
                                  <Trans>Unstake</Trans>
                                )}
                              </ButtonUnstake>
                            </RowBetween>
                          </AutoColumn>
                        ) : (
                          <ButtonPrimary size="10px" onClick={handleOpenModal}>
                            <Trans>Stake</Trans>
                          </ButtonPrimary>
                        )}
                      </AutoColumn>
                    </LightCard>
                  ) : (
                    <ButtonPrimary onClick={toggleWalletModal}>
                      <Trans>Connect Wallet</Trans>
                    </ButtonPrimary>
                  )}
                </AutoColumn>
              </Card>
            </AutoColumn>
          </ResponsiveRow>
        </AutoColumn>
      </PageWrapper>
      <SwitchLocaleLink />
      <StakeModal
        walletData={walletData}
        chainId={activeChain}
        isOpen={openModal}
        onDismiss={() => setOpenModal(false)}
      />
    </>
  )
}
